import {
  useGetAttachmentById,
  type Attachment,
} from "@/api/attachment";
import { contentColumnsGridAttrs } from "@/lib/cms-puck/blocks/column-count";
import {
  GALLERY_ASPECT_CLASS,
  normalizeAttachmentIds,
  normalizeGalleryAspect,
  normalizeGalleryColumns,
  type GalleryAspect,
} from "@/lib/cms-puck/blocks/gallery";
import { cmsNew } from "@/lib/cms/cms-new-i18n";

export function GalleryThumbCell({
  id,
  aspect,
  sampleUrl,
}: {
  id: string;
  aspect: GalleryAspect;
  /** Image d'exemple (aperçu bibliothèque uniquement) — cf. MediaPreview.sampleUrl. */
  sampleUrl?: string;
}) {
  const { data } = useGetAttachmentById(id);
  const att = data as Attachment | undefined;
  const url =
    sampleUrl ??
    att?.mediumPublicUrl ??
    att?.posterPublicUrl ??
    att?.publicUrl ??
    att?.largePublicUrl;
  const inner = url ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt={cmsNew("cms.new.auto.alt.d41d8c", "")}
      className="h-full w-full object-cover"
    />
  ) : (
    <span className="text-[10px] text-slate-400">—</span>
  );
  if (aspect !== "auto") {
    return (
      <div
        className={`overflow-hidden rounded bg-slate-100 ${GALLERY_ASPECT_CLASS[aspect]}`}
      >
        {inner}
      </div>
    );
  }
  return (
    <div className="flex max-h-40 items-center justify-center overflow-hidden rounded bg-slate-100">
      {inner}
    </div>
  );
}

export function GalleryBlockPreview({
  attachmentIds,
  columns,
  aspect,
  sampleUrls,
}: {
  attachmentIds?: string[];
  columns?: number;
  aspect?: GalleryAspect;
  /**
   * Images d'exemple (aperçu bibliothèque uniquement) utilisées quand aucune image
   * réelle n'est sélectionnée — cf. MediaPreview.sampleUrl. Jamais écrites en page.
   */
  sampleUrls?: string[];
}) {
  const ids = normalizeAttachmentIds(attachmentIds);
  const cols = normalizeGalleryColumns(columns);
  const grid = contentColumnsGridAttrs(cols);
  const asp = normalizeGalleryAspect(aspect);
  if (ids.length === 0 && sampleUrls && sampleUrls.length > 0) {
    return (
      <div className={`${grid.className} gap-3`} style={grid.style}>
        {sampleUrls.map((url, i) => (
          <GalleryThumbCell key={i} id="" aspect={asp} sampleUrl={url} />
        ))}
      </div>
    );
  }
  if (ids.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
        {cmsNew(
          "cms.new.preview.gallery.empty",
          "Galerie — choisis des médias dans le panneau de droite.",
        )}
      </div>
    );
  }
  return (
    <div className={`${grid.className} gap-3`} style={grid.style}>
      {ids.map((id) => (
        <GalleryThumbCell key={id} id={id} aspect={asp} />
      ))}
    </div>
  );
}

export function CarouselBlockPreview({
  attachmentIds,
  aspect,
}: {
  attachmentIds?: string[];
  aspect?: GalleryAspect;
}) {
  const ids = normalizeAttachmentIds(attachmentIds);
  const asp = normalizeGalleryAspect(aspect);
  if (ids.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
        {cmsNew(
          "cms.new.preview.carousel.empty",
          "Carousel — choisis des médias dans le panneau de droite.",
        )}
      </div>
    );
  }
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
      {ids.map((id) => (
        <div key={id} className="w-64 shrink-0 snap-center">
          <GalleryThumbCell id={id} aspect={asp} />
        </div>
      ))}
    </div>
  );
}
